import { debounce } from '@utils/debounce';

// Konstanty pro mobilní breakpoint a výšku při sbalení
const MOBILE_BREAKPOINT = 679;
const MOBILE_COLLAPSED_HEIGHT = 160;

interface TopFeedToggleProps {
  containerElement: HTMLElement;
  buttonElement?: HTMLButtonElement;
}

/**
 * Inicializuje přepínač Top Feed pro mobilní zařízení.
 *
 * Tato funkce umožňuje sbalování a rozbalování části obsahu na základě šířky okna.
 * Na mobilních zařízeních se obsah zkrátí a zobrazí tlačítko pro rozbalení/zavření.
 * Na desktopu je obsah vždy zobrazen celý a tlačítko se skryje.
 *
 * @param containerElement - Hlavní kontejner top feedu, který obsahuje obsah i tlačítko.
 * @param buttonElement - (Volitelně) vlastní tlačítko, pokud není určeno přes DOM atribut.
 *
 * @returns Objekt s metodami:
 *  - `toggle`: manuálně přepne stav rozbalení,
 *  - `setCollapsedState(collapsed: boolean)`: nastaví sbalení podle parametru,
 *  - `cleanup`: odstraní event listenery.
 */
export const TopFeedToggle = ({
  containerElement,
  buttonElement: optionalButton,
}: TopFeedToggleProps) => {
  // Obsahová část pro skrývání/rozbalování
  const contentElement = containerElement.querySelector(
    '[data-top-feed-toggle-content]'
  ) as HTMLElement | null;

  // Toggle tlačítko
  const buttonElement =
    optionalButton ||
    (containerElement.querySelector('[data-top-feed-toggle-button]') as HTMLButtonElement | null);

  if (!contentElement || !buttonElement) {
    console.warn('Required elements for top feed toggle not found');
    return null;
  }

  // Stav komponenty
  const state = {
    containerElement,
    contentElement,
    buttonElement,
    isCollapsed: true,
    isMobile: window.innerWidth <= MOBILE_BREAKPOINT,
  };

  // Funkce pro aktualizaci textu na tlačítku podle stavu (rozbaleno/sbaleno)
  const updateButtonText = (): void => {
    const collapseText = state.buttonElement.getAttribute('data-text-collapse');
    const expandText = state.buttonElement.getAttribute('data-text-expand');
    state.buttonElement.textContent = state.isCollapsed ? expandText : collapseText;
  };

  // Animace změny výšky obsahu
  const animateHeightChange = (from: number, to: number, callback?: () => void) => {
    state.contentElement.style.height = `${from}px`;
    void state.contentElement.offsetHeight; // Force reflow
    state.contentElement.style.height = `${to}px`;

    if (callback) {
      setTimeout(callback, 400);
    }
  };

  // Funkce pro sbalení obsahu (používá se na mobilu)
  const collapse = () => {
    state.contentElement.style.height = `${MOBILE_COLLAPSED_HEIGHT}px`;
    state.containerElement.classList.add('top-feed--collapsed');
    state.isCollapsed = true;
    state.buttonElement.style.display = 'inline-block';
  };

  // Funkce pro rozbalení obsahu
  const expand = () => {
    state.contentElement.style.height = 'auto';
    state.containerElement.classList.remove('top-feed--collapsed');
    state.isCollapsed = false;
    state.buttonElement.style.display = 'none';
  };

  // Inicializační funkce – nastaví výchozí stav podle šířky okna
  const initialize = (): void => {
    state.contentElement.style.transition = 'height 0.3s ease-in-out';
    state.contentElement.style.overflow = 'hidden';

    if (state.isMobile) {
      collapse();
    } else {
      expand();
    }

    updateButtonText();
  };

  // Přepínač mezi sbaleným a rozbaleným stavem (jen na mobilu)
  const toggle = (): void => {
    if (!state.isMobile) return;

    const startHeight = state.contentElement.offsetHeight;

    if (state.isCollapsed) {
      // Rozbalení
      state.contentElement.style.height = 'auto';
      const fullHeight = state.contentElement.offsetHeight;
      animateHeightChange(startHeight, fullHeight, () => {
        state.contentElement.style.height = 'auto';
      });
      state.containerElement.classList.remove('top-feed--collapsed');
    } else {
      // Sbalení
      animateHeightChange(startHeight, MOBILE_COLLAPSED_HEIGHT);
      state.containerElement.classList.add('top-feed--collapsed');
    }

    state.isCollapsed = !state.isCollapsed;
    updateButtonText();
  };

  // Ošetření změny velikosti okna – přepíná mezi mobilním a desktop režimem
  const handleResize = (): void => {
    const wasMobile = state.isMobile;
    const wasCollapsed = state.isCollapsed;

    state.isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

    if (wasMobile !== state.isMobile) {
      if (state.isMobile) {
        // Přepnutí do mobilního režimu - Sbalení obsahu
        state.contentElement.style.height = `${MOBILE_COLLAPSED_HEIGHT}px`;
        state.containerElement.classList.add('top-feed--collapsed');
        state.isCollapsed = true;
        state.buttonElement.style.display = 'inline-block';
      } else {
        // Přepnutí do desktop režimu - Rozbalení obsahu
        state.contentElement.style.height = 'auto';
        state.containerElement.classList.remove('top-feed--collapsed');
        state.isCollapsed = false;
        state.buttonElement.style.display = 'none';
      }

      if (wasCollapsed !== state.isCollapsed) {
        updateButtonText();
      }
    }
  };

  // Přidání Listenerů (klik a resize)
  const addEventListeners = (): void => {
    state.buttonElement.addEventListener('click', (e) => {
      e.preventDefault();
      toggle();
    });

    window.addEventListener('resize', debounce(handleResize, 250));
  };

  // Inicializace a spuštění přepínače
  initialize();
  addEventListeners();

  // Vrácení metod pro manipulaci zvenčí
  return {
    toggle,
    setCollapsedState: (collapsed: boolean) => {
      if (collapsed !== state.isCollapsed && state.isMobile) {
        toggle();
      }
    },
    cleanup: () => {
      // Odstranění event listenerů
      window.removeEventListener('resize', handleResize);
      state.buttonElement.removeEventListener('click', toggle);
    },
  };
};
